/**
 * La bozza del runtime (CF02/CF05/CF06) — CL04.
 *
 * Il nucleo, puro, dell'hook `useCustomFields`: il form carica un contesto
 * autorizzato, tiene una bozza locale, e salva **solo la differenza**.
 *
 * Le tre regole che questo modulo rende esplicite:
 *
 * 1. **il salvataggio è differenziale.** Solo i campi editabili davvero
 *    cambiati vengono inviati: rispedire la mappa visibile sovrascriverebbe i
 *    valori che il chiamante non vede con il sottoinsieme che vede.
 * 2. **un conflitto non si risolve da solo.** A revisioni nuove non si ritenta
 *    automaticamente e non si applicano in silenzio valori divenuti vietati: la
 *    bozza resta, e il confronto si mostra.
 * 3. **un valore revocato esce dalla memoria.** Quando un campo non è più
 *    accessibile, il suo valore va **eliminato** dalla bozza e dallo scarto — non
 *    mostrato nel confronto. Mostrarlo sarebbe ridivulgarlo proprio mentre
 *    l'accesso viene tolto.
 */

import type { FieldValue, RuntimeContext, RuntimeField } from "model/CustomFields";
import { valuesEqual } from "./legacyAdapter";

/** Wait for field uploads before reading the draft that their onChange fills. */
export async function finishUploadsAndSave(
  uploads: readonly Promise<unknown>[],
  save: () => Promise<boolean>
): Promise<boolean> {
  const completed = await Promise.allSettled(uploads);
  if (completed.some((result) => result.status === "rejected")) return false;
  await Promise.resolve();
  return save();
}

/** A workflow transition may proceed only after all field values are saved. */
export async function runAfterFieldsSaved(
  flush: () => Promise<boolean>,
  transition: () => void | Promise<void>
): Promise<boolean> {
  if (!(await flush())) return false;
  await transition();
  return true;
}

export interface RuntimeDraft {
  contextId: string;
  /** La revisione su cui la bozza è stata costruita: è quella che si dichiara. */
  revision: RuntimeContext["revision"];
  /** I valori correnti, editati compresi. Solo campi accessibili. */
  values: Record<string, FieldValue>;
  /** I campi che l'utente ha davvero toccato. */
  edited: ReadonlySet<string>;
}

/** I campi editabili del contesto. */
export const editableFields = (context: RuntimeContext): RuntimeField[] =>
  context.fields.filter((field) => field.access === "editable");

/** Gli id dei campi editabili. */
export const editableIds = (context: RuntimeContext): string[] =>
  editableFields(context).map((field) => field.fieldId);

/** Gli id di tutti i campi che il contesto include. */
export const visibleIds = (context: RuntimeContext): string[] =>
  context.fields.map((field) => field.fieldId);

/**
 * La bozza da un contesto.
 *
 * I valori arrivano dal server e non vengono interpretati: `null` è un valore
 * (il campo è vuoto), non un'assenza da riempire.
 */
export function createDraft(context: RuntimeContext): RuntimeDraft {
  return {
    contextId: context.contextId,
    revision: context.revision,
    values: Object.fromEntries(
      context.fields.map((field) => [field.fieldId, field.value])
    ),
    edited: new Set<string>(),
  };
}

/**
 * Aggiorna un valore nella bozza.
 *
 * Aggiorna **solo la bozza**: nessuna richiesta parte da qui, e un campo non
 * editabile non entra comunque nella patch perché `changesForSave` filtra sugli
 * editabili.
 */
export function setValue(
  draft: RuntimeDraft,
  fieldId: string,
  value: FieldValue
): RuntimeDraft {
  return {
    ...draft,
    values: { ...draft.values, [fieldId]: value },
    edited: new Set([...Array.from(draft.edited), fieldId]),
  };
}

/**
 * La patch da inviare.
 *
 * `changesForSave` non manda un campo tornato al valore di partenza: l'utente
 * l'ha toccato e riportato com'era, e rispedirlo sarebbe una modifica che non è
 * avvenuta.
 *
 * `undefined` per un campo cancellato dalla mappa — un campo che il contesto
 * non include non deve comparire.
 */
export function changesForSave(
  draft: RuntimeDraft,
  fields: readonly RuntimeField[]
): Record<string, FieldValue> {
  const changes: Record<string, FieldValue> = {};
  for (const field of fields) {
    // Il filtro sugli editabili sta **qui**, non nel chiamante: questa funzione
    // promette di restituire una patch, e una patch che contenesse un campo in
    // sola lettura verrebbe rifiutata dal server — o, se il server fosse
    // permissivo, scriverebbe un valore che il registro o la formula hanno
    // prodotto.
    if (field.access !== "editable") continue;
    if (!draft.edited.has(field.fieldId)) continue;
    const current = draft.values[field.fieldId];
    if (valuesEqual(current, field.value)) continue;
    changes[field.fieldId] = current ?? null;
  }
  return changes;
}

/** C'è qualcosa da salvare? */
export const hasChanges = (
  draft: RuntimeDraft,
  fields: readonly RuntimeField[]
): boolean => Object.keys(changesForSave(draft, fields)).length > 0;

/**
 * I campi che la bozza conosceva e il nuovo contesto non include più.
 *
 * Sono quelli diventati nascosti, o non più autorizzati: la loro esistenza non
 * va nemmeno rivelata, quindi il confronto non li mostra.
 */
export function revokedFieldIds(
  draft: RuntimeDraft,
  context: RuntimeContext
): string[] {
  const visible = new Set(visibleIds(context));
  return Object.keys(draft.values).filter((fieldId) => !visible.has(fieldId));
}

/**
 * Ribasa la bozza su un contesto nuovo.
 *
 * Usata alla riconciliazione. I valori dei campi
 * **revocati vengono eliminati**, non conservati per il confronto: mostrarli
 * mentre l'accesso viene tolto sarebbe ridivulgarli.
 */
export function acceptContext(
  draft: RuntimeDraft,
  context: RuntimeContext
): RuntimeDraft {
  const visible = new Set(visibleIds(context));
  const values: Record<string, FieldValue> = {};

  for (const field of context.fields) {
    // Un valore che l'utente ha toccato sopravvive al ribasamento solo se il
    // campo è ancora accessibile; altrimenti è il server ad avere ragione.
    values[field.fieldId] =
      draft.edited.has(field.fieldId) && visible.has(field.fieldId)
        ? (draft.values[field.fieldId] ?? null)
        : field.value;
  }

  return {
    contextId: context.contextId,
    revision: context.revision,
    values,
    // Restano "editati" soltanto i campi ancora presenti: di quelli spariti non
    // si conserva traccia.
    edited: new Set(Array.from(draft.edited).filter((fieldId) => visible.has(fieldId))),
  };
}

/**
 * Accetta la risposta di una PATCH. I valori inviati sono confermati e si prende
 * la versione normalizzata del server; solo modifiche avvenute durante la
 * richiesta restano nella bozza per la PATCH successiva.
 */
export function acceptSavedContext(
  current: RuntimeDraft,
  submitted: RuntimeDraft,
  context: RuntimeContext
): RuntimeDraft {
  const edited = new Set(
    context.fields
      .filter((field) => field.access === "editable")
      .filter((field) => current.edited.has(field.fieldId))
      .filter((field) => !valuesEqual(current.values[field.fieldId], submitted.values[field.fieldId]))
      .map((field) => field.fieldId)
  );
  return {
    contextId: context.contextId,
    revision: context.revision,
    values: Object.fromEntries(context.fields.map((field) => [
      field.fieldId,
      edited.has(field.fieldId) ? current.values[field.fieldId] ?? null : field.value,
    ])),
    edited,
  };
}

/**
 * Il confronto da mostrare su un conflitto.
 *
 * Solo i campi **ancora accessibili** che differiscono fra la bozza e il
 * contesto corrente. I revocati sono già usciti dalla memoria: non compaiono
 * qui, perché il confronto non è un canale per rivedere ciò che non si può più
 * vedere.
 */
export function conflictDiff(
  draft: RuntimeDraft,
  context: RuntimeContext
): { fieldId: string; local: FieldValue; remote: FieldValue }[] {
  return context.fields
    .filter((field) => draft.edited.has(field.fieldId))
    .filter((field) => field.access === "editable")
    .filter((field) => !valuesEqual(draft.values[field.fieldId] ?? null, field.value))
    .map((field) => ({
      fieldId: field.fieldId,
      local: draft.values[field.fieldId] ?? null,
      remote: field.value,
    }));
}

/**
 * I campi obbligatori ancora vuoti, fra quelli che l'utente può scrivere.
 *
 * Serve a spiegare *perché* un avanzamento è rifiutato senza che il client
 * decida da sé cosa è obbligatorio: la lista arriva dal contesto autorizzato,
 * che è del server.
 */
export function missingRequired(
  draft: RuntimeDraft,
  context: RuntimeContext,
  transition: "task" | "process"
): string[] {
  const flag = transition === "task" ? "requiredOnTask" : "requiredOnProcess";

  return context.fields
    .filter((field) => field.access === "editable")
    .filter((field) => field[flag] === true)
    .filter((field) => !(field.exemptions ?? []).includes(field.fieldId))
    .filter((field) => isMissing(draft.values[field.fieldId] ?? null))
    .map((field) => field.fieldId);
}

/**
 * Il valore è mancante?
 *
 * `null`, una stringa di soli spazi e un array vuoto sono mancanti; `false` e
 * `"0"` sono risposte. La stessa definizione del server, perché un client che
 * ne avesse una diversa bloccherebbe ciò che il server accetta.
 */
export function isMissing(value: FieldValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "boolean") return false;
  if (Array.isArray(value)) return value.length === 0;
  return value.trim() === "";
}
